import React, { useState, useEffect } from "react";
import { useParams } from "react-router";
import { Link, useNavigate } from "react-router-dom";
import "./OpenStory.css";
import EmptyList from "../../Common/EmptyList/EmptyList";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faThumbsUp, faEye, faStar, faLink, faCheck } from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp, faTwitter } from "@fortawesome/free-brands-svg-icons";
import axios from "axios";
import { config } from "../../../config/const";
import Rating from "@mui/material/Rating";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { toast } from "react-toastify";

const OpenStory = () => {
  const { id } = useParams();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(0);
  const [value, setValue] = useState(0);

  const [linkCopied, setLinkCopied] = useState(false);
  const email = localStorage.getItem("email");
  const isLoggedIn = !!email;
  const navigate = useNavigate();

  const checkLikedStatus = async () => {
    const email = localStorage.getItem("email");
    try {
      const response = await axios.get(`${config.backend_url}/user/checkLike`, {
        params: { story_id: id, email },
      });
      setLiked(response.data.isLiked);
    } catch (err) {
        console.log(err);
    }
  };

  const fetchStoryDetails = async () => {
    try {
      setLoading(true);
      const baseUrl = config?.backend_url || 'http://localhost:3002';
      
      const response = await axios.get(`${baseUrl}/user/story/${id}`);
      
      // Response should not be wrapped in a 'story' object anymore
      const storyData = response.data.story;
      
      if (!storyData) {
        toast.error('Story not found');
        return;
      }

      setStory(storyData);
      setLikes(storyData.likes || 0);

      // Add view after successfully setting story
      await axios.post(`${baseUrl}/user/addView`, { story_id: id });

    } catch (error) {
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      toast.error('Failed to load story');
    } finally {
      setLoading(false);
    }
  };

  const getRating = async () => {
    const email = localStorage.getItem("email");
    try {
      const response = await axios.get(
        `${config.backend_url}/user/getStoryRating`,
        { params: { story_id: id, email } }
      );
      const response2 = await axios.get(
        `${config.backend_url}/user/getUserRating`,
        { params: { story_id: id, email } }
      );
      setValue(response2.data.rating);
    } catch (err) {
      console.log(err);
    }
  };

  const setRating = async (value) => {
    const email = localStorage.getItem("email");
    try {
      await axios.post(`${config.backend_url}/user/addRating`, {
        story_id: id,
        email,
        rating: value,
      });
    } catch (err) {
      console.log(err);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  useEffect(() => {
    if (id) {
      fetchStoryDetails();
      if (email) {
        checkLikedStatus();
        getRating();
      }
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleLike = () => {
    if (!isLoggedIn) {
      navigate('/Login');
      return;
    }
    setLiked(!liked);
    axios.post(`${config.backend_url}/user/postLike`, {
      story_id: id,
      email,
    });
    setLikes(liked ? likes - 1 : likes + 1);
  };

  const currentUrl = window.location.href;

  return (
    <>
      <Link className="story-goBack" to="/">
        <span> &#8592;</span> <span>Go Back</span>
      </Link>
      {loading ? (
        <div className="loading-container">
          <div className="loader"></div>
          <p>Loading story...</p>
        </div>
      ) : story ? (
        <div className="story-wrap">
          <header>
            <h1>{story.title}</h1>
            <p className="story-author">
              Published by {story.user?.name || "Unknown"} on{" "}
              {(() => {
                const date = new Date(story.publishingDate);
                const day = date.getDate();
                const monthNames = [
                  "January",
                  "February",
                  "March",
                  "April",
                  "May",
                  "June",
                  "July",
                  "August",
                  "September",
                  "October",
                  "November",
                  "December",
                ];
                const month = monthNames[date.getMonth()];
                const year = date.getFullYear();
                const getOrdinalSuffix = (day) => {
                  if (day > 3 && day < 21) return "th";
                  switch (day % 10) {
                    case 1:
                      return "st";
                    case 2:
                      return "nd";
                    case 3:
                      return "rd";
                    default:
                      return "th";
                  }
                };
                return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
              })()}
            </p>
          </header>

          <div className="story-lines">
            {story?.storyList?.map((storyLine, i) => (
              <div key={i} className="story-line">
                {storyLine.image && <img alt="story image" src={storyLine.image} />}
                <p>{storyLine.line}</p>
              </div>
            ))}
          </div>
          {/* ── Engagement Bar ── */}
          <div className="engagement-bar">
            <div className="engagement-item">
              <button
                className={`engagement-btn like-btn ${liked ? "liked" : ""}`}
                onClick={email ? toggleLike : undefined}
                disabled={!email}
                title={email ? (liked ? "Unlike" : "Like this story") : "Sign in to like"}
              >
                <FontAwesomeIcon icon={faThumbsUp} />
                <span>{likes}</span>
              </button>
            </div>
            <div className="engagement-item views-item">
              <FontAwesomeIcon icon={faEye} className="engagement-icon" />
              <span>{story.views} views</span>
            </div>
            {email && (
              <div className="engagement-item rating-item">
                <FontAwesomeIcon icon={faStar} className="engagement-icon star-icon" />
                <Rating
                  name="simple-controlled"
                  value={value}
                  size="medium"
                  onChange={(event, newValue) => {
                    setRating(newValue);
                    setValue(newValue);
                  }}
                />
              </div>
            )}
          </div>

          {/* ── Quiz CTA Card ── */}
          <Link className="quiz-cta-card" to={`/quiz/${story._id}`}>
            <div className="quiz-cta-icon">🧠</div>
            <div className="quiz-cta-text">
              <span className="quiz-cta-title">Take the Quiz</span>
              <span className="quiz-cta-desc">Test your understanding of this story</span>
            </div>
            <span className="quiz-cta-arrow">&#8594;</span>
          </Link>

          {/* ── Share Toolbar ── */}
          <div className="share-toolbar">
            <span className="share-label">Share</span>
            <div className="share-buttons">
              <a
                href={`https://api.whatsapp.com/send?text=${encodeURIComponent("Check out this story: " + currentUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="share-btn whatsapp"
                title="Share on WhatsApp"
              >
                <FontAwesomeIcon icon={faWhatsapp} />
              </a>
              <a
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(currentUrl)}&text=${encodeURIComponent("Check out this story!")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="share-btn twitter"
                title="Share on Twitter"
              >
                <FontAwesomeIcon icon={faTwitter} />
              </a>
              <button
                className="share-btn copy-link"
                onClick={handleCopyLink}
                title="Copy link"
              >
                <FontAwesomeIcon icon={linkCopied ? faCheck : faLink} />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <EmptyList />
      )}

      {/* CTA for non-logged-in users */}
      {!isLoggedIn && story && (
        <div className="public-cta-banner">
          <h3 className="public-cta-title">Create a story like this for your child</h3>
          <p className="public-cta-desc">Personalize stories with your child's name, age, and interests — powered by AI.</p>
          <button className="public-cta-btn" onClick={() => navigate('/Login')}>
            Get Started — It's Free
          </button>
        </div>
      )}
    </>
  );
};

export default OpenStory;
